/**
 * Reports dashboard time-range presets — kept in sync with
 * `st-ck-server/.../ReportQueryBuilder.ts` (`TimeRangePreset` + `resolveTimeRange`).
 */
import { TimeRangePreset } from '../Settings/reportDsl';

export interface ReportsQuickRange {
  label: string;
  /** When set, report queries use `timeRangeOverride.preset` (calendar-aware on the server). */
  preset?: TimeRangePreset;
  /** Rolling window length ending at "now" — used for shift/zoom and client-side bounds. */
  ms?: number;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Ranges with a `preset` use the server's calendar-aware resolver; ranges with
 * only `ms` are sent as absolute rolling windows ending at "now" (the server
 * has no matching preset for 3h / 2d / 60d / 2y).
 */
export const REPORTS_QUICK_RANGES: ReportsQuickRange[] = [
  { label: 'Last 1 hour', preset: 'last_1_hour', ms: HOUR_MS },
  { label: 'Last 3 hours', ms: 3 * HOUR_MS },
  { label: 'Last 6 hours', preset: 'last_6_hours', ms: 6 * HOUR_MS },
  { label: 'Last 12 hours', preset: 'last_12_hours', ms: 12 * HOUR_MS },
  { label: 'Last 24 hours', preset: 'last_24_hours', ms: 24 * HOUR_MS },
  { label: 'Last 2 days', ms: 2 * DAY_MS },
  { label: 'Last 7 days', preset: 'last_7_days', ms: 7 * DAY_MS },
  { label: 'Last 30 days', preset: 'last_30_days', ms: 30 * DAY_MS },
  { label: 'Last 60 days', ms: 60 * DAY_MS },
  { label: 'Last 90 days', preset: 'last_90_days', ms: 90 * DAY_MS },
  { label: 'Last 6 months', preset: 'last_180_days', ms: 180 * DAY_MS },
  { label: 'Last 1 year', preset: 'last_365_days', ms: 365 * DAY_MS },
  { label: 'Last 2 years', ms: 730 * DAY_MS },
];

export const REPORTS_DEFAULT_QUICK_RANGE =
  REPORTS_QUICK_RANGES.find((r) => r.label === 'Last 24 hours') ?? REPORTS_QUICK_RANGES[0];

/** Max span for absolute From/To on the Reports page (2 years, matching the longest quick range). */
export const REPORTS_CUSTOM_TIME_MAX_MS = 730 * DAY_MS;

/** Client-side mirror of `ReportQueryBuilder.resolveTimeRange` for picker display / shift. */
export const resolveReportsPresetBounds = (preset: TimeRangePreset): { start: number; end: number } => {
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);

  switch (preset) {
    case 'last_1_hour':
      start.setHours(now.getHours() - 1);
      break;
    case 'last_6_hours':
      start.setHours(now.getHours() - 6);
      break;
    case 'last_12_hours':
      start.setHours(now.getHours() - 12);
      break;
    case 'last_24_hours':
      start.setHours(now.getHours() - 24);
      break;
    case 'last_7_days':
      start.setDate(now.getDate() - 7);
      break;
    case 'last_30_days':
      start.setDate(now.getDate() - 30);
      break;
    case 'last_90_days':
      start.setDate(now.getDate() - 90);
      break;
    case 'last_180_days':
      start.setDate(now.getDate() - 180);
      break;
    case 'last_365_days':
      start.setDate(now.getDate() - 365);
      break;
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'yesterday':
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(now.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case 'this_week': {
      const day = start.getDay();
      start.setDate(now.getDate() - day);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case 'this_month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'this_year':
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      start.setDate(now.getDate() - 30);
  }

  return { start: start.getTime(), end: end.getTime() };
};
